import { useState, useEffect, useRef, useMemo } from "react";

const SHADER_DURATION = 2000; // Phase 1: dark background + particles
const WELCOME_DURATION = 1500; // Phase 2: welcome text
const EXIT_DURATION = 500; // Phase 3: fade out

const DemoTransition = ({ show, onComplete, tenantName, logoSrc }) => {
  const [phase, setPhase] = useState("shader");
  const timerRef = useRef(null);

  useEffect(() => {
    if (!show) return;

    setPhase("shader");

    // Phase 1 -> Phase 2
    const welcomeTimer = setTimeout(() => {
      setPhase("welcome");
    }, SHADER_DURATION);

    // Phase 2 -> Phase 3
    const exitTimer = setTimeout(() => {
      setPhase("exit");
    }, SHADER_DURATION + WELCOME_DURATION);

    // Complete
    const completeTimer = setTimeout(() => {
      onComplete?.();
    }, SHADER_DURATION + WELCOME_DURATION + EXIT_DURATION);

    timerRef.current = completeTimer;

    return () => {
      clearTimeout(welcomeTimer);
      clearTimeout(exitTimer);
      clearTimeout(completeTimer);
    };
  }, [show, onComplete]);

  // Generate 25 particles with random positions and delays - stable across re-renders
  const particles = useMemo(
    () =>
      Array.from({ length: 25 }, (_, i) => ({
        id: i,
        left: `${Math.random() * 100}%`,
        top: `${Math.random() * 100}%`,
        size: 2 + Math.random() * 4,
        delay: Math.random() * 3,
        duration: 4 + Math.random() * 4,
      })),
    []
  );

  if (!show) return null;

  return (
    <div className={`demo-transition demo-transition--${phase}`}>
      {/* Animated gradient background */}
      <div className="demo-transition__gradient" />

      {/* Floating particles */}
      <div className="demo-transition__particles">
        {particles.map((p) => (
          <span
            key={p.id}
            className="demo-transition__particle"
            style={{
              left: p.left,
              top: p.top,
              width: p.size,
              height: p.size,
              animationDelay: `${p.delay}s`,
              animationDuration: `${p.duration}s`,
            }}
          />
        ))}
      </div>

      {/* Center content */}
      <div className="demo-transition__center">
        {logoSrc && (
          <img
            src={logoSrc}
            alt={tenantName || "Logo"}
            className={`demo-transition__logo ${
              phase === "shader" || phase === "welcome"
                ? "demo-transition__logo--visible"
                : ""
            }`}
          />
        )}

        <div
          className={`demo-transition__text ${
            phase === "welcome" ? "demo-transition__text--visible" : ""
          }`}
        >
          <h2>Welcome to the Buyer Listing Service</h2>
          {tenantName && <p>Powered by {tenantName}</p>}
        </div>
      </div>
    </div>
  );
};

export default DemoTransition;
